import { useRouter } from 'next/router';
import React, {
  ChangeEvent,
  FormEvent,
  useLayoutEffect,
  useState,
} from 'react';
import { handleTrack } from '../../../../utils/tracker';
import { SUCCEESS_MODAL_PROPS } from '../../../Ui/SuccessModal';
import { EVENT_TYPE } from '../../Utils/constants';
import { IWEBINAR_DATA } from '../../Utils/types';
import CleartaxLeadFormCall from './Common/leadCall';
import { WebinarLeadFormCondition } from './FormCondition';
import { FORM_ACTION, LOCAL_STORAGE } from './FormData/formConstants';
import { FORM_DATA } from './Utils/types';
import { useFormValidator } from './Utils/useFormValidator';
import { getFormattedDataForLead } from './Utils/utils';

interface WebinarLeadFormProps {
  webinarDetails: IWEBINAR_DATA;
  modalRef?: React.MutableRefObject<null>;
  eventType: EVENT_TYPE;
  formTitle: string;
  initailFormData: Record<string, FORM_DATA>;
  buttonText: string;
  onClick?: () => void;
  onSuccess?: () => void;
  successData: SUCCEESS_MODAL_PROPS;
}

export const WebinarLeadForm = ({
  webinarDetails,
  modalRef,
  eventType,
  formTitle,
  buttonText,
  onSuccess,
  successData,
  initailFormData,
}: WebinarLeadFormProps): JSX.Element => {
  const [form, setForm] = useState<Record<string, FORM_DATA>>(initailFormData);
  const [formSuccess, successfulForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { formData, validateForm, onBlurField } = useFormValidator(form);
  const router = useRouter();

  const { IS_LEAD_SUBMITTED, IS_REGISTERED_FOR_WEBINARS } = LOCAL_STORAGE;
  const { title } = webinarDetails;

  useLayoutEffect(() => {
    const isRegisteredVal = localStorage.getItem(IS_REGISTERED_FOR_WEBINARS);

    isRegisteredVal &&
    JSON.parse(isRegisteredVal).includes(window.location.pathname)
      ? successfulForm(true)
      : successfulForm(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath]);

  const onTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    const field = e.target.name;

    const nextFormState = {
      ...formData,
      [field]: {
        ...formData[field],
        value: e.target.value,
      },
    } as Record<string, FORM_DATA>;
    setForm(nextFormState);

    validateForm({
      form: nextFormState,
      field: field,
    });
  };

  const trackForm = ({
    data,
    formAction,
  }: {
    data: Record<string, FORM_DATA>;
    formAction: FORM_ACTION;
  }) => {
    const formKeys = Object.keys(data);
    const getErrors = {} as Record<string, string | undefined>;
    const getData = {} as Record<string, string | undefined>;

    formKeys.forEach((key) => {
      getErrors[key] = data[key]?.errors.message;
      getData[key] = data[key]?.value;
    });

    const value =
      formAction === FORM_ACTION.ERROR
        ? { form_errors: getErrors }
        : { ...getData };

    handleTrack({
      action: formAction,
      label: `form_${eventType}_webinar_${buttonText}`,
      value: title,
      valueObj: value,
    });
  };

  const setLocalStorage = () => {
    const isRegisteredVal = localStorage.getItem(IS_REGISTERED_FOR_WEBINARS);
    const parsedRegistration = isRegisteredVal
      ? JSON.parse(localStorage.getItem(IS_REGISTERED_FOR_WEBINARS) || '')
      : [];

    eventType === EVENT_TYPE.PAST
      ? localStorage.setItem(IS_LEAD_SUBMITTED, 'true')
      : localStorage.setItem(
          IS_REGISTERED_FOR_WEBINARS,
          JSON.stringify([...parsedRegistration, window.location.pathname])
        );
  };

  const onSubmitForm = async (e: FormEvent) => {
    const WEBINAR_TYPE = webinarDetails?.tag?.[0]?.title || '';

    e.preventDefault();
    const { isValid, updatedFormErrors } = validateForm({
      form: formData,
      forceTouchErrors: true,
    });

    trackForm({
      data: updatedFormErrors,
      formAction: !isValid ? FORM_ACTION.ERROR : FORM_ACTION.SUBMIT,
    });

    if (!isValid) return;

    setIsSubmitting(true);

    await CleartaxLeadFormCall.sendClearLeadData(
      getFormattedDataForLead(
        updatedFormErrors,
        webinarDetails,
        eventType,
        WEBINAR_TYPE
      )
    ).then((response) => {
      if (response.data.response === 'success') {
        successfulForm(true);
        onSuccess && onSuccess();

        setLocalStorage();

        trackForm({
          data: updatedFormErrors,
          formAction: FORM_ACTION.SUCCESS,
        });
      } else {
        trackForm({
          data: updatedFormErrors,
          formAction: FORM_ACTION.FAILURE,
        });
      }
    });
    setIsSubmitting(false);
  };

  return (
    <div
      className="modal rounded-xl modal-open"
      data-testid="outer-form"
      id="contact-sales"
      data-fold-location="navbar_button_Book a demo"
      ref={modalRef}
    >
      <div
        className={`${
          eventType === EVENT_TYPE.UPCOMING ? '' : 'px-10 py-8 sm:p-4'
        }`}
      >
        <WebinarLeadFormCondition
          formTitle={formTitle}
          buttonText={buttonText}
          successData={successData}
          formSuccess={formSuccess}
          onTextChange={onTextChange}
          onSubmitForm={onSubmitForm}
          onBlurField={onBlurField}
          formData={formData}
          isFormSubmitting={isSubmitting}
        />
      </div>
    </div>
  );
};
